// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Dropdown,
    Field,
    Label,
    Option,
    OptionOnSelectData,
    SelectionEvents,
    Textarea,
    makeStyles,
} from '@fluentui/react-components';
import { AppsList20Regular, Dismiss24Regular, DocumentOnePageSparkle20Regular } from '@fluentui/react-icons';
import React from 'react';
import { IFundInfo } from '../../../libs/services/DocumentImportService';

interface IDdqUploadDialogProps {
    title: string;
    open: boolean;
    funds: IFundInfo[];
    onUpload: (ddqPrompt?: string, selectedFund?: string) => void;
    onClose: () => void;
}

const useClasses = makeStyles({
    actions: {
        display: 'flex',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
    },
    fundsContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    fundsListLabelWrapper: {
        marginTop: '15px',
        marginBottom: '15px',
        display: 'flex',
    },
    fundsListLabel: {
        marginLeft: '10px',
    },
    fundsList: {
        marginBottom: '15px',
    },
    ddqPromptContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    ddqPromptLabelWrapper: {
        marginTop: '15px',
        marginBottom: '15px',
        display: 'flex',
    },
    ddqPromptLabel: {
        marginLeft: '10px',
    },
});

export const DdqUploadDialog: React.FC<IDdqUploadDialogProps> = ({ title, open, funds, onUpload, onClose }) => {
    const classes = useClasses();

    const [hint, setHint] = React.useState('Maximum 255 characters');
    const [validationMessage, setValidationMessage] = React.useState('');
    const [ddqPrompt, setDdqPrompt] = React.useState('');
    const [selectedFund, setSelectedFund] = React.useState('');

    React.useEffect(() => {
        if (open) {
            const defaultValue = funds.filter((f) => f.default === 'True');
            setSelectedFund(defaultValue.length > 0 ? defaultValue[0].title : '');
            setDdqPrompt('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onOptionSelect = (_ev: SelectionEvents, data: OptionOnSelectData) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        if (data.optionText) {
            setSelectedFund(data.optionText);
        }
    };

    const defaultFund = funds.filter((f) => f.default === 'True');

    const getDialogContent = () => {
        return (
            <div className={classes.content}>
                <div className={classes.fundsContainer}>
                    <div className={classes.fundsListLabelWrapper}>
                        <AppsList20Regular />
                        <Label htmlFor="fundsList" className={classes.fundsListLabel}>
                            Select which fund the DDQ is for:
                        </Label>
                    </div>
                    <Dropdown
                        id="fundsList"
                        className={classes.fundsList}
                        onOptionSelect={onOptionSelect}
                        value={selectedFund}
                        selectedOptions={defaultFund.length > 0 ? [defaultFund[0].id] : undefined}
                    >
                        {funds.map((fund) => (
                            <Option text={fund.title} value={fund.id} key={fund.id}>
                                {fund.title}
                            </Option>
                        ))}
                    </Dropdown>
                </div>
                <div className={classes.ddqPromptContainer}>
                    <div className={classes.ddqPromptLabelWrapper}>
                        <DocumentOnePageSparkle20Regular />
                        <Label htmlFor="ddqPrompt" className={classes.ddqPromptLabel}>
                            Additional context for the DDQ (optional):
                        </Label>
                    </div>
                    <Field hint={hint} validationMessage={validationMessage}>
                        <Textarea
                            id="ddqPrompt"
                            value={ddqPrompt}
                            style={{ resize: 'none', height: '95px' }}
                            onChange={(_e, data) => {
                                if (data.value.length <= 255) {
                                    setHint('Maximum 255 characters');
                                    setValidationMessage('');
                                } else {
                                    setHint('');
                                    setValidationMessage(
                                        'Please shorten your message to fit within the limit: maximum 255 characters',
                                    );
                                }

                                setDdqPrompt(data.value);
                            }}
                            placeholder="E.g. This is for Fund I investor, who has previously invested in Fund II. This investor cares about the details of sustainability and is concerned about succession planning"
                        />
                    </Field>
                </div>
            </div>
        );
    };

    return (
        // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
        <Dialog open={open} onOpenChange={onClose}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle
                        action={
                            <DialogTrigger action="close">
                                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                            </DialogTrigger>
                        }
                    >
                        {title}
                    </DialogTitle>
                    <DialogContent>{getDialogContent()}</DialogContent>
                    <DialogActions position="end" className={classes.actions}>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary">Close</Button>
                        </DialogTrigger>
                        <Button
                            appearance="primary"
                            onClick={() => {
                                onUpload(ddqPrompt, selectedFund);
                            }}
                        >
                            Upload
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
